import { faStar as faStarFull } from '@fortawesome/sharp-solid-svg-icons';
import { faStar as faStarOutline } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { pathName } from './projectConstant';
import { getKeyByValue, slugMapped } from './scopeMap';

export const englishToDutchDate: {
  [key: string]: string;
} = {
  January: 'januari',
  February: 'februari',
  March: 'maart',
  April: 'april',
  May: 'mei',
  June: 'juni',
  July: 'juli',
  August: 'augustus',
  September: 'september',
  October: 'oktober',
  November: 'november',
  December: 'december',
};

export const cropParagraph = (
  inputString: string,
  maxLength: number
): string => {
  if (inputString?.length > maxLength) {
    return `${inputString.substring(0, maxLength)}...`;
  }
  return inputString;
};

export function formatDate(dateString: string): string {
  const months = [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
  ];

  const date: Date = new Date(dateString);
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const currentDay = currentDate.getDate();

  const monthName = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  if (
    currentMonth === date.getMonth() &&
    currentYear === year &&
    currentDay === day + 1
  ) {
    return 'Gisteren';
  }
  const formattedDay = day < 10 ? `0${day}` : day;
  return `${formattedDay} ${monthName} ${year}`;
}

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

export const calculateOverallRating = (
  scoreDisplay: number,
  isOverall: boolean
): {
  icon: IconProp;
  color: string;
}[] => {
  const starArray = [];
  const rating = Math.round(scoreDisplay / (isOverall ? 2 : 1));
  for (let i = 1; i <= 5; i += 1) {
    if (i <= rating) {
      starArray.push({
        icon: faStarFull as IconProp,
        color: '#FFEB3A',
      });
    } else {
      starArray.push({
        icon: faStarOutline as IconProp,
        color: '#D1DFEC',
      });
    }
  }
  return starArray;
};

interface DateRangeInput {
  fromDate: string;
  toDate: string;
}

export function getAvailableYears(input: DateRangeInput): number[] {
  const fromDate = new Date(input.fromDate);
  const toDate = new Date(input.toDate);

  const yearsSet = new Set<number>();
  const currentDate = new Date(fromDate);

  while (currentDate <= toDate) {
    yearsSet.add(currentDate.getFullYear());
    currentDate.setFullYear(currentDate.getFullYear() + 1);
    currentDate.setMonth(0);
    currentDate.setDate(1);
  }

  return Array.from(yearsSet).sort((a, b) => b - a);
}

export function getAvailableMonthsForYear(
  input: DateRangeInput,
  selectedYear: number
): string[] {
  const fromDate = new Date(input.fromDate);
  const toDate = new Date(input.toDate);

  const monthsSet = new Set<string>();

  for (let month = 0; month < 12; month += 1) {
    const startOfMonth = new Date(selectedYear, month, 1);
    const endOfMonth = new Date(selectedYear, month + 1, 0);

    if (
      (startOfMonth >= fromDate && startOfMonth <= toDate) ||
      (endOfMonth >= fromDate && endOfMonth <= toDate)
    ) {
      const monthName =
        englishToDutchDate[
          startOfMonth.toLocaleString('en-US', { month: 'long' })
        ];
      monthsSet.add(monthName);
    }
  }

  return Array.from(monthsSet).sort((a, b) => {
    const dateA = new Date(`2020-${a}-01`).getMonth();
    const dateB = new Date(`2020-${b}-01`).getMonth();
    return dateA - dateB;
  });
}

export function getDateForMonthAndYear(
  month: string,
  year: number,
  fromDate: string,
  toDate: string
): Date {
  const monthsMap: { [key: string]: number } = {
    januari: 0,
    februari: 1,
    maart: 2,
    april: 3,
    mei: 4,
    juni: 5,
    juli: 6,
    augustus: 7,
    september: 8,
    oktober: 9,
    november: 10,
    december: 11,
  };

  const monthIndex = monthsMap[month];
  if (monthIndex === undefined) {
    throw new Error(`Invalid month name: ${month}`);
  }

  const specifiedDate = new Date(year, monthIndex, 10);

  const fromDateObject = new Date(fromDate);
  const toDateObject = new Date(toDate);

  if (specifiedDate >= fromDateObject && specifiedDate <= toDateObject) {
    return specifiedDate;
  }

  const endOfMonth = new Date(year, monthIndex + 1, 0);

  if (endOfMonth >= fromDateObject && endOfMonth <= toDateObject) {
    return endOfMonth;
  }

  const distanceFromStart = Math.abs(
    fromDateObject.getTime() - specifiedDate.getTime()
  );
  const distanceFromEnd = Math.abs(
    toDateObject.getTime() - specifiedDate.getTime()
  );

  if (distanceFromStart < distanceFromEnd) {
    return fromDateObject;
  }
  return toDateObject;
}

export function getMonthFromDate(toDate: string) {
  const date = new Date(toDate);
  const monthNames = [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
  ];
  const monthIndex = date.getMonth();

  const monthName = monthNames[monthIndex];

  return monthName;
}

export function getYearFromDate(toDate: string) {
  return new Date(toDate).getFullYear();
}

export const modifyTitle = (title: string): string => {
  if (!title) {
    return '';
  }
  const splittedTitle = title?.split(' ');
  const provinceName =
    splittedTitle[splittedTitle?.length ? splittedTitle.length - 1 : 0];
  const midifiedProvenceName =
    provinceName.charAt(0).toUpperCase() + provinceName.slice(1);
  splittedTitle.pop();
  return splittedTitle.join(' ').concat(`\n${midifiedProvenceName}`);
};

export function extractMonthAndYear(dateTimeString: string): {
  month: string;
  year: number;
} {
  const months = [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
  ];

  const dateParts = dateTimeString.split(' ')[0].split('-');
  const monthIndex = parseInt(dateParts[1], 10) - 1;
  const year = parseInt(dateParts[2], 10);

  const month = months[monthIndex];

  return { month, year };
}

export const getAntiForgeryToken = (): string | null => {
  const tokenElement = document.querySelector<HTMLInputElement>(
    'input[name="__RequestVerificationToken"]'
  );
  return tokenElement ? tokenElement.value : null;
};

export const capitalizeFirstLetter = (string: string): string => string.charAt(0).toUpperCase() + string.slice(1);

export const replacePriceSuffix = (str: string): string => {
  if (str.endsWith('Uur')) {
    return str.replace(/Uur$/, 'Per uur');
  }
  if (str.endsWith('uur')) {
    return str.replace(/uur$/, 'per uur');
  }
  return str;
}

export const getScopeFromURL = () => {
  const parts = pathName.split('/');
  if (parts.length >= 2) {
    return parts[1];
  }
  return null;
}

export const getScopeMapped = (setter: any) => {
  const scope = getScopeFromURL();
  let scopeMapped;
  if (scope) {
    scopeMapped = getKeyByValue(slugMapped, scope);
    if (scopeMapped) setter(scopeMapped);
  }
  return scopeMapped;
}